'use client'

import { useState } from 'react'
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog'
import { AlertDialog, AlertDialogContent, AlertDialogHeader, AlertDialogTitle, AlertDialogDescription, AlertDialogFooter, AlertDialogCancel, AlertDialogAction } from '@/components/ui/alert-dialog'
import type { MessageAgentChannel } from '@/hooks/agents/messaging/use-agents'
import type { ContactProvider } from '@/lib/agents/messaging/channels/web-widget/contact-channels/model'
import { WebWidgetSettings } from './web-widget-settings'
import { useTranslations } from "next-intl"


type Props = {
  open: boolean
  onOpenChange: (open: boolean) => void
  channel: MessageAgentChannel
  canManage: boolean
  onRefresh: () => void
  onConnect: (provider: ContactProvider) => void
}

export function WebWidgetSettingsDialog({ open, onOpenChange, channel, canManage, onRefresh, onConnect }: Props) {
  const tUi = useTranslations("Messaging.AgentsMessagingSettingsWebWidgetSettingsDialog")

  const [dirty, setDirty] = useState(false)
  const [confirmClose, setConfirmClose] = useState(false)
  const [pendingProvider, setPendingProvider] = useState<ContactProvider | null>(null)

  function requestClose(provider: ContactProvider | null = null) {
    if (dirty) {
      setPendingProvider(provider)
      setConfirmClose(true)
      return
    }
    close(provider)
  }

  function close(provider: ContactProvider | null) {
    setDirty(false)
    setConfirmClose(false)
    setPendingProvider(null)
    onOpenChange(false)
    if (provider) onConnect(provider)
  }

  return <>
    <Dialog open={open} onOpenChange={next => { if (next) onOpenChange(true); else requestClose() }}>
      <DialogContent className="max-w-5xl max-h-[85vh] overflow-y-auto">
        <DialogHeader><DialogTitle><span key={tUi("webChatSettings")}>{tUi("webChatSettings")}</span></DialogTitle><DialogDescription className="sr-only"><span key={tUi("customiseYourChatAppearanceAndContactChannels")}>{tUi("customiseYourChatAppearanceAndContactChannels")}</span></DialogDescription></DialogHeader>
        <WebWidgetSettings channel={channel} canManage={canManage} onRefresh={onRefresh} onDirtyChange={setDirty} onConnect={provider => requestClose(provider)} />
      </DialogContent>
    </Dialog>
    <AlertDialog open={confirmClose} onOpenChange={setConfirmClose}>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle><span key={tUi("leaveWithoutSaving")}>{tUi("leaveWithoutSaving")}</span></AlertDialogTitle>
          <AlertDialogDescription><span key={tUi("youHaveUnsavedChangesInWebChatSettings")}>{tUi("youHaveUnsavedChangesInWebChatSettings")}</span></AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel><span key={tUi("keepEditing")}>{tUi("keepEditing")}</span></AlertDialogCancel>
          <AlertDialogAction onClick={() => close(pendingProvider)}><span key={tUi("discardChanges")}>{tUi("discardChanges")}</span></AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  </>
}
